import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { authApi } from '@/api/auth';
import type { SessionUser } from '@/api/types';
import { STORAGE_KEY } from '@/utils/platform';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<SessionUser | null>(loadFromStorage());
  const fetchMeAttempted = ref(false);
  const isLogin = computed(() => !!user.value);
  const roleCodes = computed(() => user.value?.roleCodes ?? []);
  const permissions = computed(() => user.value?.permissions ?? []);
  const orgName = computed(() => user.value?.orgName ?? '');
  const isSuperAdmin = computed(() => !!user.value?.isSuperAdmin);
  const isSuperAdminMode = computed(() => !!user.value?.isSuperAdmin && !user.value?.orgUuid);

  function loadFromStorage(): SessionUser | null {
    try { const raw = localStorage.getItem(STORAGE_KEY); return raw ? JSON.parse(raw) : null; }
    catch { return null; }
  }

  function setUser(u: SessionUser | null) {
    user.value = u;
    if (u) localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
    else localStorage.removeItem(STORAGE_KEY);
  }

  let fetchMePromise: Promise<SessionUser | null> | null = null;

  async function fetchMe() {
    if (fetchMePromise) return fetchMePromise;
    fetchMePromise = (async () => {
      try { const u = await authApi.me(); setUser(u); return u; }
      catch { setUser(null); return null; }
      finally { fetchMePromise = null; }
    })();
    return fetchMePromise;
  }

  async function logout() { try { await authApi.logout(); } finally { setUser(null); fetchMeAttempted.value = false; } }

  function hasAnyRole(...codes: string[]) { return codes.some((c) => roleCodes.value.includes(c)); }
  function hasPermission(code: string) { return permissions.value.includes(code); }
  function hasAnyPermission(...codes: string[]) { return codes.some((c) => permissions.value.includes(c)); }
  async function switchOrg(org_uuid: string) { const u = await authApi.switchOrg(org_uuid); setUser(u); return u; }
  async function switchToSuperAdmin() { const u = await authApi.switchToSuperAdmin(); setUser(u); return u; }

  async function generateQRKey() {
    const res = await authApi.wechatQrCode();
    return res;
  }

  async function handleQRLogin(token: string) {
    const res = await authApi.wechatQrStatus(token);
    if (res.status === 'confirmed' && res.user) {
      setUser(res.user);
    }
    return res;
  }

  async function selectOrg(key: string, org_uuid: string) {
    const u = await authApi.selectOrg(key, org_uuid);
    setUser(u);
    return u;
  }

  async function login(username: string, password: string) {
    const u = await authApi.login(username, password);
    setUser(u);
    return u;
  }

  async function loginAsOrg(org_uuid: string) {
    return switchOrg(org_uuid);
  }

  return { user, isLogin, roleCodes, permissions, orgName, isSuperAdmin, isSuperAdminMode, fetchMeAttempted, fetchMe, logout, switchOrg, switchToSuperAdmin, hasAnyRole, hasPermission, hasAnyPermission, setUser, generateQRKey, handleQRLogin, selectOrg, login, loginAsOrg };
});